import React, { useEffect, useMemo, useState, useContext, useCallback } from "react";
import { Link, useRoute } from "wouter";
import { AuthContext } from "../contexts/auth";
import Footer from "../components/footer";
import PopoverComponentCustom from "../components/popover_component";
import { ExclamationTriangleIcon, ArrowLeftIcon, DocumentTextIcon, MagnifyingGlassIcon, ChevronUpDownIcon, CheckIcon } from "@heroicons/react/24/outline";
import { getLetteraById, getLetteraPreview, generateLettera, downloadLettera, lettereGenericAutocomplete, lettereAnagraficaAutocomplete } from "../services/api_lettere";
import { v2FetchEmployee } from "../services/api";
import { getCCNLListPaginated } from "../services/api_contracts_employee";
import { getCompanySettings, updateCompanySettings } from "../services/api_settings";
import WordPreviewModal from "./word_preview_modal";
import GenericSelect2AutocompleteField from "./generic_select2_autocomplete";
import LetteraEntityLookupField from "./lettera_autocomplete_modal";
import LetteraGenericLookupModal from "./lettera_generic_lookup_modal";
import LetteraCcnlAttachmentsSelect from "./lettera_ccnl_attachment_select";
import Swal from "sweetalert2";
import { Listbox } from "@headlessui/react";
import LetterFormatCustomizerBanner from "./letter_format_customizer_banner";

const sexOptions = [
  { value: "m", label: "Maschio" },
  { value: "f", label: "Femmina" },
];

/* ----------------------------- helpers ----------------------------- */

function groupFieldsBySector(fields = []) {
  const map = {};

  for (const field of fields) {
    const sectorName = field?.settore || "Generale";
    if (!map[sectorName]) map[sectorName] = [];
    map[sectorName].push(field);
  }

  const entries = Object.entries(map);

  entries.sort(([a], [b]) =>
    a.localeCompare(b, "it", { sensitivity: "base" })
  );

  for (const [, sectorFields] of entries) {
    sectorFields.sort((x, y) => (x.ordine ?? 0) - (y.ordine ?? 0));
  }

  const sections = {};
  for (const [key, value] of entries) {
    sections[key] = value;
  }

  return { sections, entries };
}

function fieldKey(field) {
  return field?.placeholder || field?.label || field?.nome || `field_${field?.id}`;
}

function getModelName(field) {
  return String(field?.anagrafica_model || "").toLowerCase();
}

function getSourceField(field) {
  return String(field?.anagrafica_field || "");
}

function hasAnagraficaMapping(field) {
  return !!(getModelName(field) && getSourceField(field));
}

function normalizeLookupValue(value) {
  if (value === undefined || value === null) return undefined;

  if (typeof value === "object") {
    return (
      value?.value ??
      value?.text ??
      value?.nome ??
      value?.name ??
      value?.label ??
      value?.descrizione ??
      value?.description ??
      value?.codice ??
      value?.code ??
      value?.id
    );
  }

  return value;
}

function getNestedValue(obj, path) {
  if (!obj || !path) return undefined;

  return String(path).split("__").reduce((acc, key) => {
    if (acc == null) return undefined;
    return acc[key];
  }, obj);
}

function getContractPath(field) {
  const sourceField = getSourceField(field);

  if (sourceField.startsWith("contracts__")) {
    return sourceField.replace(/^contracts__/, "");
  }

  if (sourceField.startsWith("contract__")) {
    return sourceField.replace(/^contract__/, "");
  }

  return sourceField;
}

function formatDateForDisplay(value) {
  if (!value || typeof value !== "string") return value;

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [yyyy, mm, dd] = value.split("-");
    return `${dd}/${mm}/${yyyy}`;
  }

  if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
    const [datePart] = value.split("T");
    const [yyyy, mm, dd] = datePart.split("-");
    return `${dd}/${mm}/${yyyy}`;
  }

  return value;
}

function isDateLikeField(field) {
  const funzione = String(field?.funzione || "").trim().toLowerCase();
  const placeholder = String(field?.placeholder || "").toLowerCase();
  const sourceField = getSourceField(field).toLowerCase();

  if (funzione === "date") return true;

  return [
    "data_inizio",
    "data_fine",
    "birth_date",
    "created_date",
    "start_date",
    "end_date",
    "d_data_invio",
  ].some((token) => placeholder.includes(token) || sourceField.includes(token));
}

function hasEmployeeIdentityField(fields = []) {
  return fields.some((field) => {
    if (getModelName(field) !== "employee") return false;
    return ["u_cf", "u_nome", "u_cognome"].includes(field?.placeholder);
  });
}

function sectionHasEmployeeFields(fields = []) {
  return fields.some((field) => getModelName(field) === "employee");
}

function normalizeCcnlRow(item) {
  if (!item) return null;

  const id = Number(item?.id ?? item?.pk ?? 0);
  if (!id) return null;

  return {
    id,
    codice: String(item?.codice ?? "").trim(),
    nome: String(
      item?.nome ??
      item?.descrizione ??
      item?.label ??
      item?.text ??
      ""
    ).trim(),
  };
}

/* ----------------------------- field classifiers ----------------------------- */

function isEmployeeDirectField(field) {
  const model = getModelName(field);
  const sourceField = getSourceField(field);
  return model === "employee" && !!sourceField && !sourceField.includes("__");
}

function isEmployeePickerField(field) {
  if (getModelName(field) !== "employee") return false;

  const sourceField = getSourceField(field);
  if (!sourceField || sourceField.includes("__")) return false;

  return ["u_nome", "u_cognome", "u_cf"].includes(field?.placeholder);
}

function isEmployeeRelatedField(field) {
  const model = getModelName(field);
  const sourceField = getSourceField(field);

  if (sourceField.startsWith("contracts__") || sourceField.startsWith("contract__")) {
    return false;
  }

  return model === "employee" && sourceField.includes("__");
}

function isCompanyDirectField(field) {
  const model = getModelName(field);
  const sourceField = getSourceField(field);
  return model === "company" && !!sourceField && !sourceField.includes("__");
}

function isCompanyRelatedField(field) {
  const model = getModelName(field);
  const sourceField = getSourceField(field);
  return model === "company" && sourceField.includes("__");
}

function isContractField(field) {
  const model = getModelName(field);
  const sourceField = getSourceField(field);

  return (
    model === "contract" ||
    sourceField.startsWith("contract__") ||
    sourceField.startsWith("contracts__")
  );
}

function isContractDateField(field) {
  if (!isContractField(field)) return false;

  const sourceField = getSourceField(field).toLowerCase();

  return (
    sourceField.includes("start_date") ||
    sourceField.includes("end_date")

  );
}

function isContractSearchField(field) {
  if (!isContractField(field)) return false;
  if (isContractDateField(field)) return false;

  const sourceField = getSourceField(field).toLowerCase();
  const placeholder = String(field?.placeholder || "").toLowerCase();

  if (
    sourceField.includes("qualification") ||
    sourceField.includes("classification_level") ||
    sourceField.includes("ccnl") ||
    sourceField.includes("typology") ||
    sourceField.includes("working_hours_type") ||
    sourceField.includes("social_security_institution") ||
    sourceField.includes("shift_type") ||
    sourceField.includes("pat_inail") ||
    placeholder.includes("mansione") ||
    placeholder.includes("livello") ||
    placeholder.includes("ccnl")
  ) {
    return true;
  }

  return !!field?.anagrafica_is_autocomplete;
}

function isDirectGenericLookupField(field) {
  if (!hasAnagraficaMapping(field)) return false;

  const model = getModelName(field);
  const sourceField = getSourceField(field);

  if (!field?.anagrafica_is_autocomplete) return false;
  if (!sourceField || sourceField.includes("__")) return false;
  if (model === "company" || model === "employee" || model === "contract") return false;

  return true;
}

function isGenericAutocompleteField(field) {
  if (!hasAnagraficaMapping(field)) return false;

  if (isContractSearchField(field)) return true;
  if (isCompanyRelatedField(field)) return true;
  if (isEmployeeRelatedField(field)) return true;

  if (isCompanyDirectField(field)) return false;
  if (isEmployeeDirectField(field)) return false;
  if (isDirectGenericLookupField(field)) return false;
  if (isContractField(field)) return false;

  return !!field?.anagrafica_is_autocomplete;
}

/* ----------------------------- field input ----------------------------- */

function FieldInput({
  field,
  value,
  onChange,
  onOpenEmployeeLookup,
  onOpenGenericLookup,
  letterId,
  fetchGenericAutocomplete,
  selectedContractCcnlId,
}) {
  const common =
    "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500";

  const funzione = (field?.funzione || "").trim() || "text";

  const shouldForwardCcnl = (() => {
    const sourceField = getSourceField(field).toLowerCase();

    return (
      sourceField.includes("classification_level") ||
      String(field?.placeholder || "").toLowerCase().includes("livello")
    );
  })();

  if (isEmployeeDirectField(field)) {
    const showLookupButton = isEmployeePickerField(field);

    return (
      <div className="relative">
        <input
          type="text"
          className={`${common} ${showLookupButton ? "pr-10" : ""}`}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
        />

        {showLookupButton && (
          <button
            type="button"
            onClick={() => onOpenEmployeeLookup(field)}
            className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-indigo-600"
            aria-label="Seleziona per cercare"
            title="Seleziona per cercare"
          >
            <MagnifyingGlassIcon className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  }

  if (isDirectGenericLookupField(field)) {
    return (
      <div className="relative">
        <input
          type="text"
          className={`${common} pr-10`}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
        />

        <button
          type="button"
          onClick={() => onOpenGenericLookup(field)}
          className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-indigo-600"
          aria-label="Seleziona per cercare"
          title="Seleziona per cercare"
        >
          <MagnifyingGlassIcon className="h-4 w-4" />
        </button>
      </div>
    );
  }

  if (isGenericAutocompleteField(field)) {
    return (
      <GenericSelect2AutocompleteField
        field={field}
        letterId={Number(letterId)}
        value={value}
        onChange={onChange}
        onPickEntity={undefined}
        placeholder="Seleziona per cercare"
        minChars={0}
        preloadOnFocus={true}
        fetcher={fetchGenericAutocomplete}
        forward={
          shouldForwardCcnl
            ? { ccnl: selectedContractCcnlId }
            : {}
        }
      />
    );
  }

  if (funzione === "textarea") {
    return (
      <textarea
        className={common}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        rows={4}
      />
    );
  }

  const isDateField = isDateLikeField(field);

  return (
    <input
      type="text"
      className={common}
      value={isDateField ? formatDateForDisplay(value ?? "") : value ?? ""}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/* ----------------------------- page ----------------------------- */

export default function LetterFormPage() {
  const { token, profile } = useContext(AuthContext);
  const [, params] = useRoute("/lettere/:id");
  const letterId = params?.id;

  const [isLoading, setIsLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [letter, setLetter] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [companyAutofillData, setCompanyAutofillData] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedEmployeeDetail, setSelectedEmployeeDetail] = useState(null);

  const [employeeLookupField, setEmployeeLookupField] = useState(null);
  const [genericLookupField, setGenericLookupField] = useState(null);

  const [useAnagrafica, setUseAnagrafica] = useState(false);

  const [submitMessage, setSubmitMessage] = useState("");
  const [submitMessageType, setSubmitMessageType] = useState("");

  const [selectedCcnl, setSelectedCcnl] = useState(null);
  const [companyDefaultCcnl, setCompanyDefaultCcnl] = useState(null);
  const [savingDefaultCcnl, setSavingDefaultCcnl] = useState(false);
  const [selectedSex, setSelectedSex] = useState("");

  const currentCompany = profile?.profile?.company || profile?.company || null;
  const companyCf = currentCompany?.cf || undefined;

  const letterFields = useMemo(() => {
    return Array.isArray(letter?.fields) ? letter.fields : [];
  }, [letter]);

  const { sections: allSections, entries: groupedEntries } = useMemo(() => {
    return groupFieldsBySector(letterFields);
  }, [letterFields]);

  const flatFields = useMemo(() => {
    return Object.values(allSections || {}).flat();
  }, [allSections]);

  const showSexSelect = useMemo(() => {
    return hasEmployeeIdentityField(flatFields) || !!selectedEmployee || !!selectedEmployeeDetail;
  }, [flatFields, selectedEmployee, selectedEmployeeDetail]);

  const employeeSectionName = useMemo(() => {
    const found = groupedEntries.find(([, fields]) => sectionHasEmployeeFields(fields));
    return found?.[0] || null;
  }, [groupedEntries]);

  const setValue = useCallback((key, value) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleFormKeyDown = useCallback((e) => {
    if (e.key !== "Enter") return;
    const tag = e.target?.tagName?.toLowerCase();
    if (tag === "textarea") return;
    e.preventDefault();
  }, []);

  useEffect(() => {
    if (!token || !letterId) return;

    let cancelled = false;

    const loadLetter = async () => {
      setIsLoading(true);
      setErr(null);

      try {
        const detail = await getLetteraById(token, Number(letterId), null);
        if (!cancelled) setLetter(detail || null);
      } catch (error) {
        if (!cancelled) setErr(error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadLetter();

    return () => {
      cancelled = true;
    };
  }, [token, letterId]);

  useEffect(() => {
    if (!token) return;

    let cancelled = false;

    const loadCompanySettings = async () => {
      try {
        const data = await getCompanySettings(token, null);
        if (cancelled) return;

        const defaultId = data?.ccnl_default_choice ? Number(data.ccnl_default_choice) : null;

        if (!defaultId) {
          setCompanyDefaultCcnl(null);
          return;
        }

        let page = 1;
        let found = null;
        let hasNext = true;

        while (hasNext && !found) {
          const response = await getCCNLListPaginated(token, "", page, 200);

          if (cancelled) return;

          const rows = Array.isArray(response?.results)
            ? response.results
            : Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response)
            ? response
            : [];

          found = rows.find(
            (item) => Number(item?.id ?? item?.pk) === Number(defaultId)
          );

          hasNext = !!response?.next;
          page += 1;
        }

        if (cancelled) return;

        setCompanyDefaultCcnl(normalizeCcnlRow(found));
      } catch (error) {
        if (!cancelled) {
          console.error("Errore caricamento settings azienda:", error);
          setCompanyDefaultCcnl(null);
        }
      }
    };

    loadCompanySettings();

    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    const rawSex =
      selectedEmployeeDetail?.sex ??
      selectedEmployee?.sex ??
      selectedEmployeeDetail?.sesso ??
      selectedEmployee?.sesso ??
      "";

    const normalized = String(rawSex).trim().toLowerCase();

    if (["m", "maschio", "male", "uomo"].includes(normalized)) {
      setSelectedSex("m");
      return;
    }

    if (["f", "femmina", "female", "donna"].includes(normalized)) {
      setSelectedSex("f");
    }
  }, [selectedEmployee, selectedEmployeeDetail]);

  useEffect(() => {
    if (!token || !currentCompany) return;

    let cancelled = false;

    const loadCompanyAutofillData = async () => {
      try {
        const searchValue =
          currentCompany?.business_name ||
          currentCompany?.ragione_sociale ||
          currentCompany?.name ||
          "";

        if (!searchValue) return;

        const response = await lettereAnagraficaAutocomplete(
          token,
          {
            model: "company",
            filter_field: "business_name",
            q: searchValue,
            page: 1,
            page_size: 20,
          },
          null
        );

        if (cancelled) return;

        const results = Array.isArray(response?.results) ? response.results : [];
        const exactMatch =
          results.find(
            (item) =>
              String(item?.fields?.business_name || "").trim().toLowerCase() ===
              String(currentCompany?.business_name || "").trim().toLowerCase()
          ) || results[0];

        setCompanyAutofillData(exactMatch?.fields || null);
      } catch (error) {
        if (!cancelled) {
          console.error("Errore caricamento dati company autofill:", error);
          setCompanyAutofillData(null);
        }
      }
    };

    loadCompanyAutofillData();

    return () => {
      cancelled = true;
    };
  }, [token, currentCompany]);

  useEffect(() => {
    if (!companyAutofillData || !flatFields.length) return;

    setFormValues((prev) => {
      const next = { ...prev };

      for (const field of flatFields) {
        if (getModelName(field) !== "company") continue;

        const key = fieldKey(field);
        const sourceField = getSourceField(field);

        if (!sourceField) continue;
        if (next[key] !== undefined && next[key] !== null && next[key] !== "") continue;

        const value = companyAutofillData[sourceField];
        if (value === undefined || value === null || value === "") continue;

        next[key] = normalizeLookupValue(value);
      }

      return next;
    });
  }, [companyAutofillData, flatFields]);

  const fetchGenericAutocomplete = useCallback(
    async ({ q, lettera_id, field, forward, page, page_size }, signal) => {
      if (!token) return { results: [], pagination: { more: false } };

      return lettereGenericAutocomplete(
        token,
        {
          q,
          lettera_id,
          field,
          forward:
            forward && typeof forward === "object"
              ? JSON.stringify(forward)
              : forward || undefined,
          page,
          page_size,
        },
        null,
        signal
      );
    },
    [token]
  );

  const contractCcnlField = useMemo(() => {
    return flatFields.find((field) => {
      const sourceField = getSourceField(field).toLowerCase();

      return (
        isContractField(field) &&
        sourceField.includes("ccnl")
      );
    });
  }, [flatFields]);

  const selectedContractCcnl = useMemo(() => {
    if (!contractCcnlField) return null;

    return formValues[fieldKey(contractCcnlField)] || null;
  }, [contractCcnlField, formValues]);

  const selectedContractCcnlId = useMemo(() => {
    if (!selectedContractCcnl || typeof selectedContractCcnl !== "object") {
      return null;
    }

    return selectedContractCcnl?.id ?? null;
  }, [selectedContractCcnl]);

  const fetchAnagraficaEntityAutocomplete = useCallback(
    async ({ model, filter_field, q, page }, signal) => {
      if (!token) return { results: [], pagination: { more: false } };

      return lettereAnagraficaAutocomplete(
        token,
        {
          model,
          filter_field,
          q,
          page,
          page_size: 20,
          company_cf: model === "employee" ? companyCf : undefined,
        },
        null,
        signal
      );
    },
    [token, companyCf]
  );

  const handlePickEntity = useCallback(
    async (model, entity) => {
      if (String(model || "").toLowerCase() !== "employee") return;

      const normalized = {
        ...(entity?.fields || {}),
        cf: entity?.fields?.cf ?? null,
        text: entity?.text ?? "",
      };

      setSelectedEmployee(normalized);

      if (!entity?.fields?.cf || !token) {
        setSelectedEmployeeDetail(null);
        return;
      }

      try {
        const detail = await v2FetchEmployee(token, entity.fields.cf, null);
        setSelectedEmployeeDetail(detail || null);
      } catch (error) {
        console.error("Errore caricamento dettaglio employee:", error);
        setSelectedEmployeeDetail(null);
      }
    },
    [token]
  );

  useEffect(() => {
    if (!flatFields.length || !selectedEmployee) return;

    setFormValues((prev) => {
      const next = { ...prev };

      for (const field of flatFields) {
        if (getModelName(field) !== "employee") continue;

        const sourceField = getSourceField(field);
        const key = fieldKey(field);

        if (!sourceField) continue;
        if (sourceField.startsWith("contracts__") || sourceField.startsWith("contract__")) continue;

        const value = selectedEmployee[sourceField];
        if (value === undefined || value === null || value === "") continue;

        next[key] = normalizeLookupValue(value);
      }

      return next;
    });
  }, [selectedEmployee, flatFields]);

  useEffect(() => {
    if (!flatFields.length || !selectedEmployeeDetail) return;

    const contractData =
      selectedEmployeeDetail?.active_contract ||
      selectedEmployeeDetail?.last_contract ||
      null;

    if (!contractData) return;

    setFormValues((prev) => {
      const next = { ...prev };

      for (const field of flatFields) {
        if (!isContractField(field)) continue;

        const sourceField = getSourceField(field);
        const key = fieldKey(field);

        if (!sourceField) continue;

        const contractPath = getContractPath(field);
        const value = getNestedValue(contractData, contractPath);

        if (value === undefined || value === null || value === "") continue;

        next[key] = normalizeLookupValue(value);
      }

      return next;
    });
  }, [selectedEmployeeDetail, flatFields]);

  const handleSaveDefaultCcnl = useCallback(async (ccnl) => {
    if (!token) return;

    const ccnlId = ccnl?.id ?? '';

    try {
      setSavingDefaultCcnl(true);

      await updateCompanySettings(token, {
        ccnl_default_choice: ccnlId,
      });

      setCompanyDefaultCcnl(
        ccnl
          ? {
              id: Number(ccnl.id),
              codice: String(ccnl.codice ?? "").trim(),
              nome: String(ccnl.nome ?? "").trim(),
            }
          : null
      );

      await Swal.fire({
        icon: "success",
        title: "Salvato!",
        text: ccnl
          ? "Il CCNL predefinito è stato salvato correttamente"
          : "Il CCNL predefinito è stato rimosso correttamente",
        timer: 3000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Errore salvataggio default CCNL:", error);

      await Swal.fire({
        icon: "error",
        title: "Errore",
        text: "Impossibile salvare il CCNL predefinito.",
        confirmButtonColor: "#dc2626",
      });
    } finally {
      setSavingDefaultCcnl(false);
    }
  }, [token]);

  const handlePreview = async () => {
    if (!letterId) return;

    setPreviewLoading(true);

    try {
      const arrayBuffer = await getLetteraPreview(token, Number(letterId), null);
      setPreviewDoc({
        name: "Anteprima",
        buffer: arrayBuffer,
        mime: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
    } catch (error) {
      console.error("Anteprima fallita:", error);
      alert("Impossibile aprire l’anteprima della lettera.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!letterId) return;

    setSubmitMessage("");
    setSubmitMessageType("");

    const fieldsPayload = {};

    for (const field of flatFields) {
      const placeholder = field?.placeholder;
      if (!placeholder) continue;

      const key = fieldKey(field);
      let value = formValues[key];

      if (value && typeof value === "object") {
        value = normalizeLookupValue(value);
      }

      if (isDateLikeField(field)) {
        value = formatDateForDisplay(value ?? "");
      }

      fieldsPayload[placeholder] = value ?? "";
    }

    const letterHasCcnlAttachment = Boolean(letter?.ccnl_applicable);

    const resolvedTipoCcnl = !letterHasCcnlAttachment
      ? ""
      : selectedCcnl?.id === 0
        ? ""
        : selectedCcnl?.codice || companyDefaultCcnl?.codice || "";

    const payload = {
      ...fieldsPayload,
      use_anagrafica: useAnagrafica,
      sesso: selectedSex,
      tipo_ccnl: resolvedTipoCcnl,
    };

    try {
      setSubmitting(true);

      const data = await generateLettera(token, Number(letterId), payload);

      const fileName =
        data?.file_name ||
        (data?.file_path || "").match(/\/([^/]+\.docx)\/?$/)?.[1];

      if (!fileName) throw new Error("Nome file non trovato");

      const res = await downloadLettera(token, Number(letterId), fileName);

      let downloadName = fileName;
      const contentDisposition = res.headers?.["content-disposition"];

      if (contentDisposition) {
        const utfMatch = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
        const plainMatch = /filename="?([^"]+)"?/i.exec(contentDisposition);

        if (utfMatch) downloadName = decodeURIComponent(utfMatch[1]);
        else if (plainMatch) downloadName = plainMatch[1];
      }

      const blobUrl = URL.createObjectURL(res.data);
      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      anchor.download = downloadName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(blobUrl);

      let uiMessage = "Documento generato con successo.";
      let uiType = "success";

      if (data?.anagrafica_success === true) {
        uiMessage =
          data?.anagrafica_message ||
          "Documento generato e anagrafica salvata/aggiornata con successo.";
        uiType = "success";
      } else if (data?.anagrafica_success === false) {
        uiMessage =
          data?.anagrafica_message ||
          "Documento generato, ma il salvataggio/aggiornamento anagrafica non è andato a buon fine.";
        uiType = "warning";
      }

      setSubmitMessage(uiMessage);
      setSubmitMessageType(uiType);

      await Swal.fire({
        icon: uiType === "success" ? "success" : "warning",
        title: uiType === "success" ? "Operazione completata" : "Attenzione",
        text: uiMessage,
        confirmButtonColor: "#4f46e5",
      });
    } catch (error) {
      console.error("Generazione/Download errore:", error);

      const errorMessage = "Errore nella generazione o nel download della lettera.";
      setSubmitMessage(errorMessage);
      setSubmitMessageType("error");

      await Swal.fire({
        icon: "error",
        title: "Errore",
        text: errorMessage,
        confirmButtonColor: "#dc2626",
      });
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    setSelectedCcnl(null);
    setSubmitMessage("");
    setSubmitMessageType("");
  }, [letterId]);

  return (
    <div className="flex min-h-screen flex-col">
      <PopoverComponentCustom title="Generazione Lettera" />

      <main className="flex-grow py-6">
        <div className="mx-auto w-full max-w-[1600px] px-4 md:px-6">
          <div className="mb-4 flex items-center justify-between">
            <Link
              to="/lettere"
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Torna all’elenco
            </Link>
          </div>

          {isLoading ? (
            <div className="space-y-4">
              <div className="h-28 animate-pulse rounded-2xl border border-gray-200 bg-white shadow-sm" />
              <div className="h-48 animate-pulse rounded-2xl border border-gray-200 bg-white shadow-sm" />
            </div>
          ) : err ? (
            <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              <ExclamationTriangleIcon className="h-5 w-5" />
              <span>Errore nel caricamento della lettera.</span>
            </div>
          ) : !letter ? (
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              Nessun dettaglio disponibile.
            </div>
          ) : (
            <>
              <header className="relative mb-4 overflow-hidden rounded-2xl border border-gray-200 bg-gradient-to-r from-indigo-50 to-white p-4 shadow-sm">
                <DocumentTextIcon
                  className="pointer-events-none absolute -bottom-2 -right-2 h-24 w-24 text-indigo-200/20"
                  aria-hidden="true"
                />

                <div className="relative z-10">
                  <div className="flex justify-between">
                    <div className="inline-flex items-center gap-2 rounded-md bg-indigo-100 px-2 py-1 text-xs font-semibold text-indigo-700">
                      CATEGORIA: {letter?.categoria_info?.nome ?? "—"}
                    </div>

                    <button
                      type="button"
                      onClick={handlePreview}
                      disabled={previewLoading}
                      className="rounded-lg bg-indigo-100 px-2 py-1 text-xs font-semibold text-indigo-700 disabled:opacity-60"
                    >
                      <MagnifyingGlassIcon className="me-1 inline-block h-4 w-4" />
                      {previewLoading ? "CARICAMENTO..." : "ANTEPRIMA"}
                    </button>
                  </div>

                  <h1 className="mt-2 text-xl font-semibold text-gray-900">
                    {letter.nome}
                  </h1>

                  {letter.descrizione ? (
                    <p className="mt-1 text-sm text-gray-600">{letter.descrizione}</p>
                  ) : null}
                </div>
              </header>
              
              <LetterFormatCustomizerBanner
                token={token}
                letter={letter}
                filePresenti={letter?.file_presenti || { uomo: false, donna: false }}
                onUploaded={() => {
                  Swal.fire({
                    icon: "success",
                    title: "Template salvato",
                    text: "Il template personalizzato è stato caricato correttamente.",
                    confirmButtonColor: "#4f46e5",
                  });
                }}
              />

              <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                <LetteraCcnlAttachmentsSelect
                  visible={!!letter?.ccnl_applicable}
                  token={token}
                  getCCNLListPaginated={getCCNLListPaginated}
                  defaultCcnl={companyDefaultCcnl}
                  value={selectedCcnl?.id ?? null}
                  onChange={setSelectedCcnl}
                  onSaveDefault={handleSaveDefaultCcnl}
                  savingDefault={savingDefaultCcnl}
                />

                <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown}>
                  {submitMessage ? (
                    <div
                      className={`mb-4 rounded-xl border px-4 py-3 text-sm ${
                        submitMessageType === "success"
                          ? "border-green-200 bg-green-50 text-green-700"
                          : submitMessageType === "warning"
                          ? "border-yellow-200 bg-yellow-50 text-yellow-700"
                          : "border-red-200 bg-red-50 text-red-700"
                      }`}
                    >
                      {submitMessage}
                    </div>
                  ) : null}

                  {groupedEntries.map(([sector, fields]) => (
                    <fieldset key={sector} className="mb-6">
                      <legend className="mb-2 flex items-center gap-2 rounded-md bg-indigo-50 py-1 pr-2">
                        <span className="inline-block h-6 w-1 rounded bg-gradient-to-b from-indigo-200 to-indigo-400" />
                        <span className="text-sm font-semibold text-gray-900">
                          {sector}
                        </span>
                      </legend>

                      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                        {fields.map((field) => (
                          <label key={field.id} className="block">
                            <span className="mb-1 block text-sm font-medium text-gray-700">
                              {field.nome}
                            </span>

                            <FieldInput
                              field={field}
                              letterId={letterId}
                              value={formValues[fieldKey(field)]}
                              onChange={(value) => setValue(fieldKey(field), value)}
                              onOpenEmployeeLookup={setEmployeeLookupField}
                              onOpenGenericLookup={setGenericLookupField}
                              fetchGenericAutocomplete={fetchGenericAutocomplete}
                              selectedContractCcnlId={selectedContractCcnlId}
                            />
                          </label>
                        ))}

                        {showSexSelect && sector === employeeSectionName ? (
                          <div className="block">
                            <span className="mb-1 block text-sm font-medium text-gray-700">
                              Sesso del lavoratore
                            </span>

                            <Listbox value={selectedSex} onChange={setSelectedSex}>
                              <div className="relative">
                                <Listbox.Button className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 pr-10 text-left text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500">
                                  {sexOptions.find((o) => o.value === selectedSex)?.label || "Seleziona"}
                                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                    <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                                  </span>
                                </Listbox.Button>

                                <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 bg-white py-1 text-sm shadow-lg focus:outline-none">
                                  {sexOptions.map((option) => (
                                    <Listbox.Option
                                      key={option.value}
                                      value={option.value}
                                      className={({ active }) =>
                                        `relative cursor-pointer select-none px-3 py-2 ${
                                          active ? "bg-indigo-50 text-indigo-700" : "text-gray-900"
                                        }`
                                      }
                                    >
                                      {({ selected }) => (
                                        <div className="flex items-center justify-between">
                                          <span className={selected ? "font-semibold" : ""}>
                                            {option.label}
                                          </span>

                                          {selected && (
                                            <CheckIcon className="h-4 w-4 text-indigo-600" />
                                          )}
                                        </div>
                                      )}
                                    </Listbox.Option>
                                  ))}
                                </Listbox.Options>
                              </div>
                            </Listbox>
                          </div>
                        ) : null}
                      </div>
                    </fieldset>
                  ))}
                  <hr />
                  <div className="mt-6 flex justify-center">
                    {/* <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={useAnagrafica}
                        onChange={(e) => setUseAnagrafica(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span>Salva/aggiorna anagrafica</span>
                    </label> */}
                    <div className="flex flex-wrap items-center justify-center gap-3">
                      <Link
                        to="/lettere"
                        className="inline-flex w-48 items-center justify-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                      >
                        Annulla
                      </Link>

                      <button
                        type="submit"
                        disabled={submitting}
                        className="inline-flex w-48 items-center justify-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-60"
                      >
                        {submitting ? "Generazione..." : "Genera lettera"}
                      </button>
                    </div>
                  </div>

                </form>
              </section>

              <LetteraEntityLookupField
                open={!!employeeLookupField}
                field={employeeLookupField}
                onClose={() => setEmployeeLookupField(null)}
                onPickEntity={(model, entity) => {
                  handlePickEntity(model, entity);

                  if (employeeLookupField) {
                    const typedValue =
                      entity?.fields?.[employeeLookupField?.anagrafica_field] ??
                      entity?.text ??
                      "";

                    setValue(fieldKey(employeeLookupField), typedValue);
                  }

                  setEmployeeLookupField(null);
                }}
                fetcher={fetchAnagraficaEntityAutocomplete}
              />

              <LetteraGenericLookupModal
                open={!!genericLookupField}
                field={genericLookupField}
                letterId={letterId}
                onClose={() => setGenericLookupField(null)}
                onPickItem={(item) => {
                  if (genericLookupField) {
                    const typedValue = item?.value ?? item?.text ?? "";
                    setValue(fieldKey(genericLookupField), typedValue);
                  }

                  setGenericLookupField(null);
                }}
                fetcher={fetchGenericAutocomplete}
              />

              {previewDoc && (
                <WordPreviewModal
                  doc={previewDoc}
                  onClose={() => setPreviewDoc(null)}
                />
              )}
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
